import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  TrendingUp, Wallet, Package, Trophy, AlertTriangle, Users2, Truck, FileText, Download, FileSpreadsheet, BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { API } from "@/lib/api";

const RED = "#E51921";
const PIE = ["#71717A", "#2563EB", "#16A34A", "#DC2626"];

function Stat({ icon: Icon, label, value, hint, accent }) {
  return (
    <div className="bg-white border border-border rounded-md p-5 fade-up" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
      </div>
      <div className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight tabular">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [sop, setSop] = useState(null);
  const { can, permissions } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setS(data)).catch(() => {});
    if (can("sops")) api.get("/sops/compliance/overview").then(({ data }) => setSop(data)).catch(() => {});
  }, [permissions]);

  if (!s)
    return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  return (
    <div>
      <PageHeader title="Command Centre" subtitle="Live snapshot across sales, tenders and inventory.">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-black hover:bg-black/85 text-white transition-colors" data-testid="export-report-button">
              <Download className="h-4 w-4 mr-1.5" /> Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => window.open(`${API}/reports/dashboard/pdf`, "_blank")} data-testid="export-pdf">
              <FileText className="h-4 w-4 mr-2" /> Management Report (PDF)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.open(`${API}/reports/inventory/excel`, "_blank")} data-testid="export-inventory-excel">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Inventory (Excel)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.open(`${API}/reports/sales/excel`, "_blank")} data-testid="export-sales-excel">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Sales (Excel)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Wallet} label="Today's Sales" value={money(s.daily_sales)} hint="Gas POS" accent />
        <Stat icon={TrendingUp} label="Monthly Revenue" value={money(s.revenue)} hint={<span data-testid="revenue-split">Gas POS {money(s.gas_revenue ?? 0)} · PPE/Quotes {money(s.ppe_revenue ?? 0)}</span>} />
        <Stat icon={Trophy} label="Tender Success" value={`${s.tender_success_rate}%`} hint={`${s.tenders_won ?? 0} won / ${s.tenders_lost ?? 0} lost`} />
        <Stat icon={Package} label="Inventory Value" value={money(s.inventory_value)} hint={`${s.total_products} active SKUs`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 bg-white border border-border rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">Sales — Last 7 Days</h3>
            <span className="text-xs text-muted-foreground">POS turnover</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={s.sales_series} margin={{ left: -10, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#52525B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#52525B" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 6, border: "1px solid #E4E4E7", fontSize: 13 }} />
              <Line type="monotone" dataKey="sales" stroke={RED} strokeWidth={2.5} dot={{ r: 3, fill: RED }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-border rounded-md p-5">
          <h3 className="font-display text-lg font-semibold mb-4">Quotation Pipeline</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={s.quote_status_breakdown} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {s.quote_status_breakdown.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E4E4E7", fontSize: 13 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {s.quote_status_breakdown.map((q, i) => (
              <div key={q.name} className="flex items-center gap-2 text-xs">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: PIE[i % PIE.length] }} />
                <span className="text-muted-foreground">{q.name}</span>
                <span className="ml-auto font-semibold tabular">{q.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="bg-white border border-border rounded-md p-5">
          <h3 className="font-display text-lg font-semibold mb-4">Top Selling Products</h3>
          {s.top_products.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No POS sales yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={s.top_products} layout="vertical" margin={{ left: 10, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#52525B" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10, fill: "#52525B" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E4E4E7", fontSize: 13 }} />
                <Bar dataKey="qty" fill={RED} radius={[0, 3, 3, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-border rounded-md p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-primary" /> Low Stock Alerts
            </h3>
            <button className="text-xs text-primary font-medium hover:underline" onClick={() => navigate("/inventory")}>View inventory →</button>
          </div>
          {s.low_stock_items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">All stock levels healthy.</p>
          ) : (
            <div className="divide-y divide-border">
              {s.low_stock_items.map((p) => (
                <div key={p.id} className="flex items-center gap-3 py-2.5">
                  <span className="text-xs font-mono text-muted-foreground w-20 shrink-0">{p.sku}</span>
                  <span className="text-sm flex-1 truncate">{p.name}</span>
                  <span className="text-xs text-muted-foreground">Reorder: {p.reorder_level}</span>
                  <span className="text-sm font-bold text-primary tabular w-14 text-right">{p.stock_quantity}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {sop && (
        <div className="bg-white border border-border rounded-md p-5 mt-4" data-testid="sop-compliance-widget">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" /> SOP Compliance
            </h3>
            <button className="text-xs text-primary font-medium hover:underline" onClick={() => navigate("/sops")}>Manage SOPs →</button>
          </div>
          <div className="grid sm:grid-cols-4 gap-4">
            <div className="sm:col-span-1 flex flex-col items-center justify-center border border-border rounded-md p-4">
              <div className="text-4xl font-display font-bold tabular text-primary" data-testid="overall-compliance">{sop.overall_compliance}%</div>
              <div className="text-xs text-muted-foreground mt-1 text-center">Company-wide sign-off</div>
              <div className="text-[11px] text-muted-foreground mt-2 text-center">{sop.published} published · {sop.staff} staff · {sop.onboarding_count} onboarding</div>
            </div>
            <div className="sm:col-span-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Outstanding sign-offs</div>
              {sop.outstanding.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Everyone is fully signed off.</p>
              ) : (
                <div className="divide-y divide-border max-h-48 overflow-y-auto">
                  {sop.outstanding.slice(0, 6).map((o) => (
                    <div key={o.sop_id} className="flex items-center gap-3 py-2" data-testid={`outstanding-sop-${o.sop_id}`}>
                      <span className="text-sm flex-1 truncate">{o.title}{o.onboarding && <span className="ml-2 text-[10px] text-primary font-medium">New Starter</span>}</span>
                      <span className="text-xs text-muted-foreground truncate max-w-[45%] hidden sm:inline" title={o.pending.join(", ")}>{o.pending.slice(0, 3).join(", ")}{o.pending.length > 3 ? ` +${o.pending.length - 3}` : ""}</span>
                      <span className="text-sm font-bold text-primary tabular w-8 text-right">{o.pending_count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Stat icon={Users2} label="Customers" value={s.total_customers} />        <Stat icon={Truck} label="Suppliers" value={s.total_suppliers} />
        <Stat icon={FileText} label="Quotations" value={s.quotations_total} hint={`${s.quotations_draft} draft`} />
        <Stat icon={AlertTriangle} label="Stock Alerts" value={s.low_stock_count} accent />
      </div>
    </div>
  );
}
